import React, { useState, useEffect } from 'react';
import API from '../../services/api';
import { THEME } from '../../constants/theme';

const ETOStatusUpdate = ({ caseId, onBack }) => {
    const [loading, setLoading] = useState(true);
    const [caseData, setCaseData] = useState(null);
    const [history, setHistory] = useState([]);
    const [isDetailsLocked, setIsDetailsLocked] = useState(false);

    // Bike Search Suggestions State
    const [bikeSearchQuery, setBikeSearchQuery] = useState('');
    const [bikeSuggestions, setBikeSuggestions] = useState([]);

    // Initial Details Form State (Added registration_type & special_number)
    const [initialForm, setInitialForm] = useState({
        bike_id: '',
        office: 'Islamabad',
        registration_type: 'New',
        special_number: '',
        old_reg_no: '',
        applicant_name: '',
        applicant_cnic: ''
    });

    // Status Update Form State
    const [selectedStatus, setSelectedStatus] = useState('Pending for submission');
    const [remarks, setRemarks] = useState('');

    // Case 3 Expense Fields
    const [expenses, setExpenses] = useState({
        voucher_amount: '',
        smart_card_plate_fee: '',
        dealing_fee: '',
        special_number_fee: '',
        lab_adj_fee: '',
        cash_paid: ''
    });

    // Case 4 Checkbox Fields
    const [recItems, setRecItems] = useState({
        rec_file: false,
        rec_card: false,
        rec_plate: false
    });

    useEffect(() => {
        fetchStatusDetails();
    }, [caseId]);

    const fetchStatusDetails = async () => {
        setLoading(true);
        try {
            const res = await API.get(`/v1/eto/cases/${caseId}/status-details`);
            if (res.data.success) {
                const info = res.data.data.case_info;
                setCaseData(info);
                setHistory(res.data.data.history || []);

                if (res.data.data.has_initial_details) {
                    setIsDetailsLocked(true);
                    const details = res.data.data.initial_details;
                    setInitialForm({
                        bike_id: details.bike_id || '',
                        office: details.office || 'Islamabad',
                        registration_type: details.registration_type || 'New',
                        special_number: details.special_number || '',
                        old_reg_no: details.old_reg_no || '',
                        applicant_name: details.applicant_name || '',
                        applicant_cnic: details.applicant_cnic || ''
                    });
                    if (details.model_name) {
                        setBikeSearchQuery(`${details.model_name} (Eng: ${details.engine_number}, Chas: ${details.chassis_number})`);
                    }
                } else {
                    setIsDetailsLocked(false);
                    setInitialForm(prev => ({ ...prev, applicant_name: info.customer_name || '' }));
                }
            }
        } catch (err) {
            console.error("Error fetching status details:", err);
            alert("Failed to load case status details");
        } finally {
            setLoading(false);
        }
    };

    // Handle Bike Search via API
    useEffect(() => {
        const fetchBikeSuggestions = async () => {
            if (bikeSearchQuery.length < 2 || isDetailsLocked) {
                setBikeSuggestions([]);
                return;
            }
            try {
                const res = await API.get(`/v1/eto/bikes/search?query=${bikeSearchQuery}`);
                if (res.data.success) {
                    setBikeSuggestions(res.data.data);
                }
            } catch (err) {
                console.error("Error searching bikes:", err);
            }
        };

        const timer = setTimeout(fetchBikeSuggestions, 300);
        return () => clearTimeout(timer);
    }, [bikeSearchQuery, isDetailsLocked]);

    // Auto Calculations for Case 3
    const totalExpenses = (
        Number(expenses.voucher_amount || 0) +
        Number(expenses.smart_card_plate_fee || 0) +
        Number(expenses.dealing_fee || 0) +
        Number(expenses.special_number_fee || 0) +
        Number(expenses.lab_adj_fee || 0) +
        Number(expenses.cash_paid || 0)
    );
    const caseIncome = Number(caseData?.income || 0);
    const profitLoss = caseIncome - totalExpenses;

    const handleSaveInitialDetails = async (e) => {
        e.preventDefault();
        try {
            const res = await API.post('/v1/eto/cases/initial-details', {
                case_id: caseId,
                ...initialForm
            });
            if (res.data.success) {
                alert("Initial details saved and locked successfully!");
                fetchStatusDetails();
            }
        } catch (err) {
            alert(err.response?.data?.error || "Error saving details");
        }
    };

    const handleStatusSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                case_id: caseId,
                status: selectedStatus,
                remarks: remarks,
                ...(selectedStatus === 'Expected date of recieving' ? {
                    ...expenses,
                    total_expenses: totalExpenses,
                    profit_loss: profitLoss
                } : {}),
                ...(selectedStatus === 'Document Recieved' ? recItems : {})
            };

            const res = await API.post('/v1/eto/cases/status-history', payload);
            if (res.data.success) {
                alert("Status updated successfully!");
                setRemarks('');
                setExpenses({ voucher_amount: '', smart_card_plate_fee: '', dealing_fee: '', special_number_fee: '', lab_adj_fee: '', cash_paid: '' });
                setRecItems({ rec_file: false, rec_card: false, rec_plate: false });
                fetchStatusDetails();
            }
        } catch (err) {
            alert(err.response?.data?.error || "Error updating status");
        }
    };

    if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading Case Details...</div>;

    return (
        <div style={{ padding: '20px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button onClick={onBack} style={{ padding: '8px 16px', backgroundColor: '#64748b', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                        ← Back to Cases
                    </button>
                    <h3 style={{ margin: 0, color: '#1e293b' }}>ETO Case Status Wizard — Case #{caseId}</h3>
                </div>
                <div style={{ padding: '8px 16px', backgroundColor: '#e2e8f0', borderRadius: '8px', fontWeight: 'bold', color: '#334155' }}>
                    Case Income: <span style={{ color: '#059669' }}>Rs. {caseData?.income || 0}</span>
                </div>
            </div>

            {/* STEP 1: INITIAL DETAILS FORM */}
            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '25px' }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#1e293b', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px' }}>
                    Step 1: Vehicle & Applicant Details {isDetailsLocked && <span style={{ fontSize: '12px', color: '#e11d48', marginLeft: '10px' }}>(Locked / Read Only)</span>}
                </h4>

                <form onSubmit={handleSaveInitialDetails}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>
                        
                        {/* BIKE SEARCH BY CHASSIS / ENGINE */}
                        <div style={{ position: 'relative' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Search Bike (Chassis / Engine)</label>
                            <input
                                type="text"
                                disabled={isDetailsLocked}
                                placeholder="Type Chassis or Engine No..."
                                value={bikeSearchQuery}
                                onChange={(e) => setBikeSearchQuery(e.target.value)}
                                style={{ 
                                    width: '100%', 
                                    padding: '9px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #cbd5e1', 
                                    backgroundColor: isDetailsLocked ? '#f1f5f9' : '#fff',
                                    color: isDetailsLocked ? '#334155' : '#000',
                                    cursor: isDetailsLocked ? 'not-allowed' : 'text'
                                }}
                            />
                            {bikeSuggestions.length > 0 && !isDetailsLocked && (
                                <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', maxHeight: '150px', overflowY: 'auto', listStyle: 'none', padding: 0, margin: '2px 0 0 0', zIndex: 100, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                {bikeSuggestions.map(bike => (
                                    <li 
                                        key={bike.id} 
                                        onClick={() => {
                                            setInitialForm({ ...initialForm, bike_id: bike.id });
                                            setBikeSearchQuery(`${bike.model_name} (Eng: ${bike.engine_number}, Chas: ${bike.chassis_number})`);
                                            setBikeSuggestions([]);
                                        }}
                                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        <strong>{bike.model_name}</strong> — Eng: {bike.engine_number} | Chas: {bike.chassis_number}
                                    </li>
                                ))}
                                </ul>
                            )}
                        </div>

                        {/* REGISTRATION TYPE (New / Transfer) */}
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Registration Type *</label>
                            <select
                                disabled={isDetailsLocked}
                                value={initialForm.registration_type}
                                onChange={(e) => setInitialForm({ ...initialForm, registration_type: e.target.value })}
                                style={{ 
                                    width: '100%', 
                                    padding: '9px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #cbd5e1', 
                                    backgroundColor: isDetailsLocked ? '#f1f5f9' : '#fff',
                                    color: isDetailsLocked ? '#334155' : '#000',
                                    cursor: isDetailsLocked ? 'not-allowed' : 'pointer'
                                }}
                                required
                            >
                                <option value="New">New</option>
                                <option value="Transfer">Transfer</option>
                            </select>
                        </div>

                        {/* SPECIAL NUMBER */}
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Special Number</label>
                            <input
                                type="text"
                                disabled={isDetailsLocked}
                                placeholder="Enter Special Number (if any)"
                                value={initialForm.special_number}
                                onChange={(e) => setInitialForm({ ...initialForm, special_number: e.target.value })}
                                style={{ 
                                    width: '100%', 
                                    padding: '9px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #cbd5e1', 
                                    backgroundColor: isDetailsLocked ? '#f1f5f9' : '#fff',
                                    color: isDetailsLocked ? '#334155' : '#000',
                                    cursor: isDetailsLocked ? 'not-allowed' : 'text'
                                }}
                            />
                        </div>

                        {/* OFFICE */}
                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Office *</label>
                            <select
                                disabled={isDetailsLocked}
                                value={initialForm.office}
                                onChange={(e) => setInitialForm({ ...initialForm, office: e.target.value })}
                                style={{ 
                                    width: '100%', 
                                    padding: '9px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #cbd5e1', 
                                    backgroundColor: isDetailsLocked ? '#f1f5f9' : '#fff',
                                    color: isDetailsLocked ? '#334155' : '#000',
                                    cursor: isDetailsLocked ? 'not-allowed' : 'pointer'
                                }}
                                required
                            >
                                <option value="Islamabad">Islamabad</option>
                                <option value="Punjab">Punjab</option>
                                <option value="KPK">KPK</option>
                                <option value="Sindh">Sindh</option>
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Old Registration No</label>
                            <input
                                type="text"
                                disabled={isDetailsLocked}
                                placeholder="Enter Old Reg No"
                                value={initialForm.old_reg_no}
                                onChange={(e) => setInitialForm({ ...initialForm, old_reg_no: e.target.value })}
                                style={{ 
                                    width: '100%', 
                                    padding: '9px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #cbd5e1', 
                                    backgroundColor: isDetailsLocked ? '#f1f5f9' : '#fff',
                                    color: isDetailsLocked ? '#334155' : '#000',
                                    cursor: isDetailsLocked ? 'not-allowed' : 'text'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Applicant Name *</label>
                            <input
                                type="text"
                                disabled={isDetailsLocked}
                                placeholder="Name"
                                value={initialForm.applicant_name}
                                onChange={(e) => setInitialForm({ ...initialForm, applicant_name: e.target.value })}
                                style={{ 
                                    width: '100%', 
                                    padding: '9px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #cbd5e1', 
                                    backgroundColor: isDetailsLocked ? '#f1f5f9' : '#fff',
                                    color: isDetailsLocked ? '#334155' : '#000',
                                    cursor: isDetailsLocked ? 'not-allowed' : 'text'
                                }}
                                required
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>CNIC *</label>
                            <input
                                type="text"
                                disabled={isDetailsLocked}
                                placeholder="CNIC Number"
                                value={initialForm.applicant_cnic}
                                onChange={(e) => setInitialForm({ ...initialForm, applicant_cnic: e.target.value })}
                                style={{ 
                                    width: '100%', 
                                    padding: '9px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #cbd5e1', 
                                    backgroundColor: isDetailsLocked ? '#f1f5f9' : '#fff',
                                    color: isDetailsLocked ? '#334155' : '#000',
                                    cursor: isDetailsLocked ? 'not-allowed' : 'text'
                                }}
                                required
                            />
                        </div>
                    </div>

                    {!isDetailsLocked && (
                        <button type="submit" style={{ marginTop: '15px', padding: '9px 20px', backgroundColor: THEME.secondary || '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                            Save & Proceed to Status Update
                        </button>
                    )}
                </form>
            </div>

            {/* STEP 2: STATUS UPDATE OPERATIONS */}
            {isDetailsLocked ? (
                <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '25px' }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#1e293b', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px' }}>
                        Step 2: Update Case Status
                    </h4>

                    <form onSubmit={handleStatusSubmit}>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Select Status Stage *</label>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 'bold' }}
                            >
                                <option value="Pending for submission">1. Pending for submission</option>
                                <option value="Process in case">2. Process in case</option>
                                <option value="Expected date of recieving">3. Expected date of recieving (Financials & Profit/Loss)</option>
                                <option value="Document Recieved">4. Document Recieved</option>
                            </select>
                        </div>

                        {/* CASE 3: EXPENSE INPUTS */}
                        {selectedStatus === 'Expected date of recieving' && (
                            <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                                <h5 style={{ margin: '0 0 10px 0', color: '#334155' }}>Financial Expenses Breakdown</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                                    <div>
                                        <label style={{ fontSize: '12px' }}>Voucher Amount</label>
                                        <input type="number" value={expenses.voucher_amount} onChange={e => setExpenses({ ...expenses, voucher_amount: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '12px' }}>Smart Card + Plate Fee</label>
                                        <input type="number" value={expenses.smart_card_plate_fee} onChange={e => setExpenses({ ...expenses, smart_card_plate_fee: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '12px' }}>Dealing Fees</label>
                                        <input type="number" value={expenses.dealing_fee} onChange={e => setExpenses({ ...expenses, dealing_fee: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '12px' }}>Special Number Fee</label>
                                        <input type="number" value={expenses.special_number_fee} onChange={e => setExpenses({ ...expenses, special_number_fee: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '12px' }}>Lab + Adj Fee</label>
                                        <input type="number" value={expenses.lab_adj_fee} onChange={e => setExpenses({ ...expenses, lab_adj_fee: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '12px' }}>Cash Paid</label>
                                        <input type="number" value={expenses.cash_paid} onChange={e => setExpenses({ ...expenses, cash_paid: e.target.value })} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px', padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                    <div><strong>Total Expenses:</strong> Rs. {totalExpenses}</div>
                                    <div><strong>Case Received Income:</strong> Rs. {caseIncome}</div>
                                    <div style={{ color: profitLoss >= 0 ? '#059669' : '#e11d48', fontWeight: 'bold' }}>
                                        {profitLoss >= 0 ? 'Profit: ' : 'Loss: '} Rs. {Math.abs(profitLoss)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CASE 4: RECEIVING CHECKBOXES */}
                        {selectedStatus === 'Document Recieved' && (
                            <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px' }}>
                                <h5 style={{ margin: '0 0 10px 0', color: '#334155' }}>Select Received Items</h5>
                                <div style={{ display: 'flex', gap: '20px' }}>
                                    <label style={{ cursor: 'pointer', fontWeight: '600' }}>
                                        <input type="checkbox" checked={recItems.rec_file} onChange={e => setRecItems({ ...recItems, rec_file: e.target.checked })} /> File
                                    </label>
                                    <label style={{ cursor: 'pointer', fontWeight: '600' }}>
                                        <input type="checkbox" checked={recItems.rec_card} onChange={e => setRecItems({ ...recItems, rec_card: e.target.checked })} /> Card
                                    </label>
                                    <label style={{ cursor: 'pointer', fontWeight: '600' }}>
                                        <input type="checkbox" checked={recItems.rec_plate} onChange={e => setRecItems({ ...recItems, rec_plate: e.target.checked })} /> Number Plate
                                    </label>
                                </div>
                            </div>
                        )}

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Remarks</label>
                            <textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Enter status remarks..."
                                style={{ width: '100%', padding: '9px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            />
                        </div>

                        <button type="submit" style={{ padding: '10px 24px', backgroundColor: '#0284c7', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                            Update Status Stage
                        </button>
                    </form>
                </div>
            ) : (
                <div style={{ padding: '15px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '8px', marginBottom: '25px', fontWeight: '600' }}>
                    ⚠️ Step 2 (Status Update) unlocks after you fill and save Step 1 details.
                </div>
            )}

            {/* STEP 3: HISTORY TIMELINE DISPLAY */}
            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#1e293b', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px' }}>
                    Status History & Timeline
                </h4>

                {history.length === 0 ? (
                    <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>No status history recorded yet.</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', color: '#64748b' }}>
                                <th style={{ padding: '10px' }}>Date</th>
                                <th style={{ padding: '10px' }}>Status</th>
                                <th style={{ padding: '10px' }}>Details / Financials</th>
                                <th style={{ padding: '10px' }}>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(item => (
                                <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{new Date(item.action_date).toLocaleDateString()}</td>
                                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#2563eb' }}>{item.status}</td>
                                    <td style={{ padding: '10px' }}>
                                        {item.status === 'Expected date of recieving' && (
                                            <div>
                                                Total Exp: <strong>Rs. {item.total_expenses}</strong> | P/L: <strong style={{ color: item.profit_loss >= 0 ? '#059669' : '#e11d48' }}>Rs. {item.profit_loss}</strong>
                                            </div>
                                        )}
                                        {item.status === 'Document Recieved' && (
                                            <div>
                                                Items: {[item.rec_file && 'File', item.rec_card && 'Card', item.rec_plate && 'Number Plate'].filter(Boolean).join(', ') || 'None'}
                                            </div>
                                        )}
                                        {item.status !== 'Expected date of recieving' && item.status !== 'Document Recieved' && '-'}
                                    </td>
                                    <td style={{ padding: '10px' }}>{item.remarks || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ETOStatusUpdate;